// YOSHI-10464: the cURL, JavaScript, Python, PHP and C# snippets of every operation, generated from
// the single endpoint definition of ./endpoints.ts.
//
// Nothing here is written by hand on purpose: five snippets copied per operation would be ninety
// snippets to keep in step with the contract, and half of them would be lying within a month. The
// sample call of the definition is the only input, so changing a path or a parameter changes the
// five languages at once.
//
// No snippet carries a pasted secret: the access token and the credentials are read from process
// variables in the idiom of each language, and the placeholders of the definition are what gets
// swapped for those variables.

import {
  DocsCodeLanguage,
  DocsCodeSample,
  DocsEndpoint,
  DocsSyntax,
} from "../interfaces";
import {
  ACCESS_TOKEN_PLACEHOLDER,
  CLIENT_ID_PLACEHOLDER,
  CLIENT_SECRET_PLACEHOLDER,
  HEADER_EXAMPLE_VALUES,
  API_BASE_URL,
} from "./endpoints";

/** The five languages every operation is documented in, in the order the tabs show them. */
export const DOCS_CODE_LANGUAGES: DocsCodeLanguage[] = [
  "curl",
  "javascript",
  "python",
  "php",
  "csharp",
];

/** What a reader who has never chosen a language sees first. */
export const DEFAULT_DOCS_CODE_LANGUAGE: DocsCodeLanguage = "curl";

/** Grammar each language is painted with. cURL is a shell command, so it reads as bash. */
export const SYNTAX_BY_CODE_LANGUAGE: Record<DocsCodeLanguage, DocsSyntax> = {
  curl: "bash",
  javascript: "javascript",
  python: "python",
  php: "php",
  csharp: "csharp",
};

/** Environment variables every snippet reads, so the five languages tell the same story. */
const ACCESS_TOKEN_ENV: string = "YOSHI_ACCESS_TOKEN";
const CLIENT_ID_ENV: string = "YOSHI_CLIENT_ID";
const CLIENT_SECRET_ENV: string = "YOSHI_CLIENT_SECRET";

/** The expressions a language uses for each value that must never be pasted into a snippet. */
interface LanguageVariables {
  accessToken: string;
  clientId: string;
  clientSecret: string;
}

interface SampleHeader {
  name: string;
  value: string;
}

const CURL_VARIABLES: LanguageVariables = {
  accessToken: "$ACCESS_TOKEN",
  clientId: "$CLIENT_ID",
  clientSecret: "$CLIENT_SECRET",
};

const JAVASCRIPT_VARIABLES: LanguageVariables = {
  // The interpolations below belong to the emitted JavaScript, not to this file.
  // eslint-disable-next-line no-template-curly-in-string
  accessToken: "${accessToken}",
  clientId: "clientId",
  clientSecret: "clientSecret",
};

const PYTHON_VARIABLES: LanguageVariables = {
  accessToken: "{access_token}",
  clientId: "client_id",
  clientSecret: "client_secret",
};

const PHP_VARIABLES: LanguageVariables = {
  accessToken: "$accessToken",
  clientId: "$clientId",
  clientSecret: "$clientSecret",
};

const CSHARP_VARIABLES: LanguageVariables = {
  accessToken: "{accessToken}",
  clientId: "clientId",
  clientSecret: "clientSecret",
};

/** Replaces every placeholder of the definition with the variable of one language. */
const resolveVariables = (
  text: string,
  variables: LanguageVariables
): string => {
  return text
    .split(ACCESS_TOKEN_PLACEHOLDER)
    .join(variables.accessToken)
    .split(CLIENT_ID_PLACEHOLDER)
    .join(variables.clientId)
    .split(CLIENT_SECRET_PLACEHOLDER)
    .join(variables.clientSecret);
};

/**
 * YOSHI-10464: emits a body value for a language that does not quote it on its own.
 *
 * A placeholder resolves to a variable of the emitted code and must stay bare; anything else is a
 * literal and has to be quoted, or the snippet reads it as an identifier and fails at run time.
 * Today every body value happens to be a placeholder, so the distinction is invisible: the day a
 * literal field is added, cURL would keep working (it quotes unconditionally) while the other four
 * languages would emit snippets that do not even parse.
 */
const resolveBodyValue = (
  value: string,
  variables: LanguageVariables,
  quote: string
): string => {
  const blnIsPlaceholder: boolean =
    value === ACCESS_TOKEN_PLACEHOLDER ||
    value === CLIENT_ID_PLACEHOLDER ||
    value === CLIENT_SECRET_PLACEHOLDER;

  if (blnIsPlaceholder) {
    return resolveVariables(value, variables);
  }

  return quote + value + quote;
};

const getHeaders = (endpoint: DocsEndpoint): SampleHeader[] => {
  return endpoint.params
    .filter((param) => param.location === "header")
    .map((param) => ({
      name: param.name,
      value: HEADER_EXAMPLE_VALUES[param.name],
    }));
};

const getQueryEntries = (endpoint: DocsEndpoint): [string, string][] => {
  return Object.entries(endpoint.example.query);
};

const getBodyEntries = (endpoint: DocsEndpoint): [string, string][] => {
  if (endpoint.example.body === null) {
    return [];
  }

  return Object.entries(endpoint.example.body);
};

const needsAccessToken = (endpoint: DocsEndpoint): boolean => {
  return getHeaders(endpoint).some((header) =>
    header.value.includes(ACCESS_TOKEN_PLACEHOLDER)
  );
};

const needsCredentials = (endpoint: DocsEndpoint): boolean => {
  return getBodyEntries(endpoint).length > 0;
};

/**
 * cURL. The call is split so no line runs off the block: `--get` with `--data-urlencode` puts one
 * query parameter per line instead of a single kilometre-long url.
 */
export const buildCurlSample = (endpoint: DocsEndpoint): string => {
  const queryEntries = getQueryEntries(endpoint);
  const bodyEntries = getBodyEntries(endpoint);
  const target = '"$BASE_URL' + endpoint.example.path + '"';
  const parts: string[] = [];

  if (endpoint.method === "POST") {
    parts.push("curl --request POST " + target);
  } else if (queryEntries.length > 0) {
    parts.push("curl --get " + target);
  } else {
    parts.push("curl " + target);
  }

  getHeaders(endpoint).forEach((header) => {
    const value = resolveVariables(header.value, CURL_VARIABLES);
    parts.push('--header "' + header.name + ": " + value + '"');
  });

  queryEntries.forEach(([name, value]) => {
    parts.push('--data-urlencode "' + name + "=" + value + '"');
  });

  if (bodyEntries.length > 0) {
    const fields = bodyEntries
      .map(([name, value]) => {
        return (
          '\\"' +
          name +
          '\\": \\"' +
          resolveVariables(value, CURL_VARIABLES) +
          '\\"'
        );
      })
      .join(", ");

    parts.push('--data "{' + fields + '}"');
  }

  const lines: string[] = [
    'BASE_URL="' + API_BASE_URL + '"',
    "",
    parts.join(" \\\n  "),
  ];

  return lines.join("\n");
};

/** JavaScript with the native fetch, no client library to install. */
export const buildJavaScriptSample = (endpoint: DocsEndpoint): string => {
  const queryEntries = getQueryEntries(endpoint);
  const bodyEntries = getBodyEntries(endpoint);
  const lines: string[] = [
    'const BASE_URL = "' + API_BASE_URL + '";',
  ];

  if (needsAccessToken(endpoint)) {
    lines.push("const accessToken = process.env." + ACCESS_TOKEN_ENV + ";");
  }

  if (needsCredentials(endpoint)) {
    lines.push("const clientId = process.env." + CLIENT_ID_ENV + ";");
    lines.push("const clientSecret = process.env." + CLIENT_SECRET_ENV + ";");
  }

  lines.push("");

  if (queryEntries.length > 0) {
    lines.push(
      // eslint-disable-next-line no-template-curly-in-string
      "const url = new URL(`${BASE_URL}" + endpoint.example.path + "`);"
    );
    lines.push("url.search = new URLSearchParams({");
    queryEntries.forEach(([name, value]) => {
      lines.push('  "' + name + '": "' + value + '",');
    });
    lines.push("}).toString();");
  } else {
    // eslint-disable-next-line no-template-curly-in-string
    lines.push("const url = `${BASE_URL}" + endpoint.example.path + "`;");
  }

  lines.push("");
  lines.push("const response = await fetch(url, {");
  lines.push('  method: "' + endpoint.method + '",');
  lines.push("  headers: {");

  getHeaders(endpoint).forEach((header) => {
    const value = resolveVariables(header.value, JAVASCRIPT_VARIABLES);
    const literal = header.value.includes(ACCESS_TOKEN_PLACEHOLDER)
      ? "`" + value + "`"
      : '"' + value + '"';
    lines.push('    "' + header.name + '": ' + literal + ",");
  });

  lines.push("  },");

  if (bodyEntries.length > 0) {
    const fields = bodyEntries
      .map(([name, value]) => {
        return name + ": " + resolveBodyValue(value, JAVASCRIPT_VARIABLES, '"');
      })
      .join(", ");

    lines.push("  body: JSON.stringify({ " + fields + " }),");
  }

  lines.push("});");
  lines.push("");
  lines.push("if (!response.ok) {");
  // eslint-disable-next-line no-template-curly-in-string
  lines.push("  throw new Error(`Yoshi API ${response.status}`);");
  lines.push("}");
  lines.push("");
  lines.push("const data = await response.json();");
  lines.push("console.log(data);");

  return lines.join("\n");
};

/** Python with requests, which is what an integration in this language reaches for. */
export const buildPythonSample = (endpoint: DocsEndpoint): string => {
  const queryEntries = getQueryEntries(endpoint);
  const bodyEntries = getBodyEntries(endpoint);
  const lines: string[] = ["import os", "", "import requests", ""];

  lines.push('BASE_URL = "' + API_BASE_URL + '"');

  if (needsAccessToken(endpoint)) {
    lines.push('access_token = os.environ["' + ACCESS_TOKEN_ENV + '"]');
  }

  if (needsCredentials(endpoint)) {
    lines.push('client_id = os.environ["' + CLIENT_ID_ENV + '"]');
    lines.push('client_secret = os.environ["' + CLIENT_SECRET_ENV + '"]');
  }

  lines.push("");
  lines.push(
    "response = requests." +
      endpoint.method.toLowerCase() +
      "("
  );
  lines.push('    f"{BASE_URL}' + endpoint.example.path + '",');
  lines.push("    headers={");

  getHeaders(endpoint).forEach((header) => {
    const value = resolveVariables(header.value, PYTHON_VARIABLES);
    const literal = header.value.includes(ACCESS_TOKEN_PLACEHOLDER)
      ? 'f"' + value + '"'
      : '"' + value + '"';
    lines.push('        "' + header.name + '": ' + literal + ",");
  });

  lines.push("    },");

  if (queryEntries.length > 0) {
    lines.push("    params={");
    queryEntries.forEach(([name, value]) => {
      lines.push('        "' + name + '": "' + value + '",');
    });
    lines.push("    },");
  }

  if (bodyEntries.length > 0) {
    lines.push("    json={");
    bodyEntries.forEach(([name, value]) => {
      lines.push(
        '        "' + name + '": ' + resolveBodyValue(value, PYTHON_VARIABLES, '"') + ","
      );
    });
    lines.push("    },");
  }

  lines.push("    timeout=30,");
  lines.push(")");
  lines.push("response.raise_for_status()");
  lines.push("");
  lines.push("print(response.json())");

  return lines.join("\n");
};

/** PHP with the curl extension, available in any standard installation. */
export const buildPhpSample = (endpoint: DocsEndpoint): string => {
  const queryEntries = getQueryEntries(endpoint);
  const bodyEntries = getBodyEntries(endpoint);
  const lines: string[] = ["<?php", ""];

  lines.push('$baseUrl = "' + API_BASE_URL + '";');

  if (needsAccessToken(endpoint)) {
    lines.push('$accessToken = getenv("' + ACCESS_TOKEN_ENV + '");');
  }

  if (needsCredentials(endpoint)) {
    lines.push('$clientId = getenv("' + CLIENT_ID_ENV + '");');
    lines.push('$clientSecret = getenv("' + CLIENT_SECRET_ENV + '");');
  }

  lines.push("");

  let target: string = '"$baseUrl' + endpoint.example.path + '"';

  if (queryEntries.length > 0) {
    lines.push("$query = http_build_query([");
    queryEntries.forEach(([name, value]) => {
      lines.push('    "' + name + '" => "' + value + '",');
    });
    lines.push("]);");
    lines.push("");
    target = '"$baseUrl' + endpoint.example.path + '?$query"';
  }

  lines.push("$curl = curl_init(" + target + ");");
  lines.push("curl_setopt($curl, CURLOPT_RETURNTRANSFER, true);");
  lines.push("curl_setopt($curl, CURLOPT_HTTPHEADER, [");

  getHeaders(endpoint).forEach((header) => {
    const value = resolveVariables(header.value, PHP_VARIABLES);
    lines.push('    "' + header.name + ": " + value + '",');
  });

  lines.push("]);");

  if (bodyEntries.length > 0) {
    lines.push("curl_setopt($curl, CURLOPT_POST, true);");
    lines.push("curl_setopt($curl, CURLOPT_POSTFIELDS, json_encode([");
    bodyEntries.forEach(([name, value]) => {
      lines.push(
        '    "' + name + '" => ' + resolveBodyValue(value, PHP_VARIABLES, '"') + ","
      );
    });
    lines.push("]));");
  }

  lines.push("");
  lines.push("$response = curl_exec($curl);");
  lines.push("curl_close($curl);");
  lines.push("");
  lines.push("print_r(json_decode($response, true));");

  return lines.join("\n");
};

/** C# with HttpClient. The query string is concatenated so no line runs off the block. */
export const buildCSharpSample = (endpoint: DocsEndpoint): string => {
  const queryEntries = getQueryEntries(endpoint);
  const bodyEntries = getBodyEntries(endpoint);
  const lines: string[] = ["using System;", "using System.Net.Http;"];

  if (bodyEntries.length > 0) {
    lines.push("using System.Text;");
    lines.push("using System.Text.Json;");
  }

  lines.push("");
  lines.push('var baseUrl = "' + API_BASE_URL + '";');

  if (needsAccessToken(endpoint)) {
    lines.push(
      'var accessToken = Environment.GetEnvironmentVariable("' +
        ACCESS_TOKEN_ENV +
        '");'
    );
  }

  if (needsCredentials(endpoint)) {
    lines.push(
      'var clientId = Environment.GetEnvironmentVariable("' +
        CLIENT_ID_ENV +
        '");'
    );
    lines.push(
      'var clientSecret = Environment.GetEnvironmentVariable("' +
        CLIENT_SECRET_ENV +
        '");'
    );
  }

  lines.push("");
  lines.push("using var client = new HttpClient();");

  // Content-Type belongs to the content in this client: adding it to the default headers throws.
  getHeaders(endpoint)
    .filter((header) => header.name !== "Content-Type")
    .forEach((header) => {
      const value = resolveVariables(header.value, CSHARP_VARIABLES);
      const literal = header.value.includes(ACCESS_TOKEN_PLACEHOLDER)
        ? '$"' + value + '"'
        : '"' + value + '"';
      lines.push(
        'client.DefaultRequestHeaders.Add("' +
          header.name +
          '", ' +
          literal +
          ");"
      );
    });

  lines.push("");

  if (queryEntries.length > 0) {
    lines.push('var url = $"{baseUrl}' + endpoint.example.path + '" +');
    queryEntries.forEach(([name, value], index) => {
      const separator = index === 0 ? "?" : "&";
      const isLast = index === queryEntries.length - 1;
      lines.push(
        '    "' + separator + name + "=" + value + '"' + (isLast ? ";" : " +")
      );
    });
  } else {
    lines.push('var url = $"{baseUrl}' + endpoint.example.path + '";');
  }

  lines.push("");

  if (bodyEntries.length > 0) {
    const fields = bodyEntries
      .map(([name, value]) => {
        return name + " = " + resolveBodyValue(value, CSHARP_VARIABLES, '"');
      })
      .join(", ");

    lines.push("var payload = new StringContent(");
    lines.push(
      "    JsonSerializer.Serialize(new { " + fields + " }),"
    );
    lines.push("    Encoding.UTF8,");
    lines.push('    "application/json");');
    lines.push("");
    lines.push("var response = await client.PostAsync(url, payload);");
  } else {
    lines.push("var response = await client.GetAsync(url);");
  }

  lines.push("response.EnsureSuccessStatusCode();");
  lines.push("");
  lines.push("Console.WriteLine(await response.Content.ReadAsStringAsync());");

  return lines.join("\n");
};

/** The five snippets of one operation, in the order the tabs show them. */
export const buildCodeSamples = (endpoint: DocsEndpoint): DocsCodeSample[] => {
  return [
    { language: "curl", label: "cURL", code: buildCurlSample(endpoint) },
    {
      language: "javascript",
      label: "JavaScript",
      code: buildJavaScriptSample(endpoint),
    },
    { language: "python", label: "Python", code: buildPythonSample(endpoint) },
    { language: "php", label: "PHP", code: buildPhpSample(endpoint) },
    { language: "csharp", label: "C#", code: buildCSharpSample(endpoint) },
  ];
};
